import { api } from '@/api/client'

export const REGULAR_CREDIT_TYPE = '00000000-0000-0000-0000-000000000001'
export type CreditType = {
  id: string
  code: string
  name: string
  is_active: boolean
}
export type CreditBenefit = {
  credit_type_id: string
  credit_name?: string
  kind: 'balance' | 'weekly' | 'unlimited'
  amount: number | null
}
export type ProductCreditDetails = {
  id: string
  required_course_id: string | null
  can_purchase: boolean
  benefits: CreditBenefit[]
}
export type TypedCreditBalance = {
  week_start: string
  credit_type_id: string
  credit_name: string
  credit_code: string
  has_subscription_allowance?: boolean
  has_unlimited_subscription: boolean
  available_subscription_credits: number | null
  available_balance_credits: number
}
export type CourseRequirements = {
  course_id: string
  course_name: string
  credit_type_id: string
  max_guests_per_booking: number
  requires_approval: boolean
  is_approved: boolean
  terms_version_id: string | null
  terms_body: string | null
  terms_required: boolean
  terms_accepted: boolean
}
export type Guest = {
  id: string
  name: string | null
  status: string
  is_approved: boolean
  is_self: boolean
}
export type BookingParticipants = {
  booking_id: string
  course_id: string
  max_guests_per_booking: number
  is_host: boolean
  host_name: string | null
  host_approved: boolean
  guests: Guest[]
  credit_type_id: string
  subscription_credits_used: number
  balance_credits_used: number
}
export type GuestInvitation = {
  id: string
  booking_id: string
  status: string
  course_id: string
  course_name: string
  host_name: string | null
  session_id: string
  starts_at: string
  ends_at: string
  is_past: boolean
}
export type RevocationPreview = {
  confirmation: string
  bookings: { id: string; starts_at: string; course_name: string }[]
  guests: { id: string; starts_at: string; status: string; name?: string | null }[]
  subscriptions: { id: string; name: string }[]
}
export type CourseRules = {
  id: string
  name: string
  credit_type_id: string
  requires_approval: boolean
  max_guests_per_booking: number
  requires_terms: boolean
  current_terms_version_id: string | null
}
export type CourseRulesDraft = {
  credit_type_id: string
  requires_approval: boolean
  max_guests_per_booking: number
  requires_terms: boolean
  terms_body: string
}

export async function getCreditTypes(): Promise<CreditType[]> {
  const { data } = await api.GET('/api/v1/credit-types')
  return data!
}
export async function saveCreditType(
  input: Partial<CreditType> & { code: string; name: string },
) {
  if (input.id) {
    await api.PUT('/api/v1/credit-types/{id}', {
      params: { path: { id: input.id } },
      body: { name: input.name, is_active: input.is_active ?? true },
    })
  } else {
    await api.POST('/api/v1/credit-types', { body: { code: input.code, name: input.name } })
  }
}
export function toCreditBenefit(row: { credit_type_id: string; credit_name: string | null; kind: string; amount: number | null }): CreditBenefit {
  return {
    credit_type_id: row.credit_type_id,
    credit_name: row.credit_name ?? undefined,
    kind: row.kind as CreditBenefit['kind'],
    amount: row.amount,
  }
}
export async function getCourseRequirements(
  input: { courseId?: string; sessionId?: string },
): Promise<CourseRequirements> {
  const { data } = input.sessionId
    ? await api.GET('/api/v1/sessions/{id}/course-requirements', { params: { path: { id: input.sessionId } } })
    : await api.GET('/api/v1/courses/{id}/requirements', { params: { path: { id: input.courseId! } } })
  return data!
}
export async function acceptCourseTerms(courseId: string, versionId: string): Promise<void> {
  await api.POST('/api/v1/courses/{id}/terms-acceptances', {
    params: { path: { id: courseId } },
    body: { terms_version_id: versionId },
  })
}
export async function getTypedCreditBalances(weeks: string[], userId?: string): Promise<TypedCreditBalance[]> {
  const { data } = userId
    ? await api.GET('/api/v1/users/{id}/credit-balances', { params: { path: { id: userId }, query: { weeks } } })
    : await api.GET('/api/v1/users/me/credit-balances', { params: { query: { weeks } } })
  return data!
}
export async function getBookingParticipants(bookingId: string): Promise<BookingParticipants> {
  const { data } = await api.GET('/api/v1/bookings/{id}/participants', { params: { path: { id: bookingId } } })
  return data!
}
export async function getGuestInvitations(includePast = false): Promise<GuestInvitation[]> {
  const { data } = await api.GET('/api/v1/users/me/guest-invitations', { params: { query: { includePast } } })
  return data!
}
export async function inviteBookingGuest(bookingId: string, email: string): Promise<string> {
  const { data } = await api.POST('/api/v1/bookings/{id}/guests', { params: { path: { id: bookingId } }, body: { email } })
  return data!.id
}
export async function reinviteBookingGuest(invitationId: string): Promise<string> {
  const { data } = await api.POST('/api/v1/guest-invitations/{id}/reinvitation', { params: { path: { id: invitationId } } })
  return data!.id
}
export async function respondToGuestInvitation(
  invitationId: string,
  action: 'accept' | 'decline' | 'withdraw',
): Promise<void> {
  await api.POST('/api/v1/guest-invitations/{id}/response', { params: { path: { id: invitationId } }, body: { action } })
}
export async function getCourseRules(): Promise<CourseRules[]> {
  const { data } = await api.GET('/api/v1/courses/rules')
  return data!
}
export async function saveCourseRules(courseId: string, rules: CourseRulesDraft): Promise<void> {
  await api.PUT('/api/v1/courses/{id}/rules', { params: { path: { id: courseId } }, body: rules })
}
export async function getCourseAccess(
  userId: string,
): Promise<{ course_id: string; is_approved: boolean; updated_at: string }[]> {
  const { data } = await api.GET('/api/v1/users/{id}/course-access', { params: { path: { id: userId } } })
  return data!
}
export async function previewCourseAccessRevocation(userId: string, courseId: string): Promise<RevocationPreview> {
  const { data } = await api.GET('/api/v1/users/{id}/course-access/{courseId}/revocation-preview', {
    params: { path: { id: userId, courseId } },
  })
  return data!
}
export async function setCourseAccess(userId: string, courseId: string, approved: boolean, confirmation?: string): Promise<void> {
  await api.PUT('/api/v1/users/{id}/course-access/{courseId}', {
    params: { path: { id: userId, courseId } },
    body: { is_approved: approved, confirmation: confirmation ?? null },
  })
}
export async function getCalendarDefault(): Promise<string | null> {
  const { data } = await api.GET('/api/v1/settings/calendar-default-resource')
  return data!.resource_id ?? null
}
export async function setCalendarDefault(resourceId: string | null) {
  await api.PUT('/api/v1/settings/calendar-default-resource', { body: { resource_id: resourceId } })
}

export function formatBenefit(benefit: CreditBenefit) {
  const name = benefit.credit_name ?? 'Credits'
  return benefit.kind === 'unlimited'
    ? `${name}: unbegrenzt`
    : `${name}: ${benefit.amount?.toLocaleString('de-AT')}${
      benefit.kind === 'weekly' ? ' / Woche' : ''
    }`
}

export type ConditionsSnapshot = {
  benefits: CreditBenefit[]
  price_cents: number
  stripe_price_id: string | null
  required_course_id: string | null
}
export type ConditionsPreview = {
  confirmation: string
  target: ConditionsSnapshot
  subscriptions: {
    id: string
    name: string
    effective_at: string
    current: ConditionsSnapshot
    conflict: string | null
    affected_bookings: { id: string; course_name: string; starts_at: string }[]
  }[]
}
export async function previewPackageConditions(
  packageId: string,
  conditions?: ConditionsSnapshot,
): Promise<ConditionsPreview> {
  const { data } = await api.POST('/api/v1/subscription-packages/{id}/conditions-preview', {
    params: { path: { id: packageId } },
    body: (conditions
      ? {
        benefits: conditions.benefits.map(({ credit_type_id, kind, amount }) => ({ credit_type_id, kind, amount })),
        price_cents: conditions.price_cents,
        stripe_price_id: conditions.stripe_price_id,
        required_course_id: conditions.required_course_id,
      }
      : null) as never,
  })
  return data as unknown as ConditionsPreview
}
